import React, { useEffect, useRef, useState, useCallback } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { MapPin, Radio, Navigation, Clock, Wrench } from "lucide-react";

const techIcon = (online, onSite) => {
  const color = onSite ? "#10b981" : online ? "#2563eb" : "#94a3b8";
  const pulse = onSite || online;
  return L.divIcon({
    className: "",
    html: `<div style="position:relative">
      <div style="width:34px;height:34px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>
      <div style="position:absolute;top:8px;left:8px;width:14px;height:14px;border-radius:50%;background:white"></div>
      ${pulse ? `<div style="position:absolute;top:-2px;left:-2px;width:38px;height:38px;border-radius:50%;border:2px solid ${color};opacity:.5;animation:fsm-pulse 1.6s infinite"></div>` : ""}
    </div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
  });
};

const fmtAgo = (iso, lang) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return lang === "en" ? "just now" : "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  return `${Math.floor(diff / 3600)}h`;
};

export default function MapPage() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const isManager = ["admin", "manager"].includes(user.role);
  const mapRef = useRef(null);
  const mapEl = useRef(null);
  const markers = useRef({});
  const [locations, setLocations] = useState([]);
  const [sharing, setSharing] = useState(false);
  const watchId = useRef(null);
  const lastPost = useRef(0);

  // init map once
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { zoomControl: true }).setView([42.3601, -71.0589], 11);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  // manager: poll locations
  useEffect(() => {
    if (!isManager) return;
    const load = () => api.get("/locations").then(setLocations).catch(() => {});
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [isManager]);

  // render markers
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const seen = new Set();
    const pts = [];
    locations.forEach((loc) => {
      seen.add(loc.user_id);
      const online = (Date.now() - new Date(loc.updated_at).getTime()) / 1000 < 120;
      const onSite = loc.status === "on_site";
      const since = new Date(loc.updated_at).toLocaleTimeString(lang === "en" ? "en-US" : "pt-BR", { hour: "2-digit", minute: "2-digit" });
      const work = onSite && loc.ticket_title
        ? `<div style="margin-top:4px;color:#059669;font-weight:600">🛠 ${t("workingOn")}: ${loc.ticket_title}</div><div style="color:#64748b">${t("since")} ${since}</div>`
        : "";
      const html = `<b>${loc.name}</b><br/>${t("activeTickets")}: ${loc.active_tickets}<br/>${t("lastSeen")}: ${fmtAgo(loc.updated_at, lang)}${work}`;
      if (markers.current[loc.user_id]) {
        markers.current[loc.user_id].setLatLng([loc.lat, loc.lng]).setIcon(techIcon(online, onSite)).bindPopup(html);
      } else {
        markers.current[loc.user_id] = L.marker([loc.lat, loc.lng], { icon: techIcon(online, onSite) }).addTo(map).bindPopup(html);
      }
      pts.push([loc.lat, loc.lng]);
    });
    Object.keys(markers.current).forEach((uid) => {
      if (!seen.has(uid)) { map.removeLayer(markers.current[uid]); delete markers.current[uid]; }
    });
    if (pts.length) map.fitBounds(pts, { padding: [60, 60], maxZoom: 14 });
  }, [locations]); // eslint-disable-line

  const postLocation = useCallback((pos) => {
    const now = Date.now();
    if (now - lastPost.current < 6000) return;
    lastPost.current = now;
    const { latitude, longitude, accuracy } = pos.coords;
    api.post("/locations", { lat: latitude, lng: longitude, accuracy }).catch(() => {});
    const map = mapRef.current;
    if (map) {
      if (markers.current.me) markers.current.me.setLatLng([latitude, longitude]);
      else markers.current.me = L.marker([latitude, longitude], { icon: techIcon(true) }).addTo(map).bindPopup(t("shareLocation"));
      map.setView([latitude, longitude], 14);
    }
  }, [t]);

  const toggleShare = () => {
    if (sharing) {
      if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
      setSharing(false);
      api.del("/locations/me").catch(() => {});
      toast.success(t("stopSharing"));
      return;
    }
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    watchId.current = navigator.geolocation.watchPosition(
      postLocation,
      () => toast.error(t("locationOff")),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
    setSharing(true);
    toast.success(t("sharing"));
  };

  useEffect(() => () => { if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current); }, []);

  const onlineCount = locations.filter((l) => (Date.now() - new Date(l.updated_at).getTime()) / 1000 < 120).length;

  return (
    <div className="space-y-5">
      <style>{`@keyframes fsm-pulse{0%{transform:scale(.6);opacity:.7}100%{transform:scale(1.4);opacity:0}}`}</style>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">{isManager ? t("tracking") : t("map")}</h1>
          {isManager && <p className="text-slate-500 text-sm mt-1">{onlineCount} {t("onlineTechs")}</p>}
        </div>
        {!isManager && (
          <button
            data-testid="share-location-btn"
            onClick={toggleShare}
            className={`inline-flex items-center gap-2 rounded-lg text-sm font-medium px-4 py-2.5 transition-colors ${sharing ? "bg-red-600 text-white hover:bg-red-700" : "bg-blue-600 text-white hover:bg-blue-700"}`}
          >
            <Radio className={`w-4 h-4 ${sharing ? "animate-pulse" : ""}`} />
            {sharing ? t("stopSharing") : t("shareLocation")}
          </button>
        )}
      </div>

      {!isManager && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-800 flex items-start gap-3">
          <Navigation className="w-5 h-5 shrink-0 mt-0.5" />
          <span>{sharing ? t("sharing") : t("techPosition")}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
        <div className={isManager ? "lg:col-span-3" : "lg:col-span-4"}>
          <div ref={mapEl} data-testid="live-map" className="w-full rounded-xl border border-slate-200 shadow-sm" style={{ height: "560px", zIndex: 0 }} />
        </div>

        {isManager && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-2 max-h-[560px] overflow-y-auto">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500 mb-1">{t("technicians")}</h2>
            {locations.length === 0 && (
              <div className="text-sm text-slate-400 py-8 text-center">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                {t("locationOff")}
              </div>
            )}
            {locations.map((l) => {
              const online = (Date.now() - new Date(l.updated_at).getTime()) / 1000 < 120;
              return (
                <button
                  key={l.user_id}
                  data-testid={`tech-loc-${l.user_id}`}
                  onClick={() => { mapRef.current?.setView([l.lat, l.lng], 15); markers.current[l.user_id]?.openPopup(); }}
                  className="w-full text-left p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-900 text-sm">{l.name}</span>
                    {l.status === "on_site"
                      ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />{t("onSite")}</span>
                      : <span className={`w-2 h-2 rounded-full ${online ? "bg-emerald-500" : "bg-slate-300"}`} />}
                  </div>
                  {l.status === "on_site" && l.ticket_title && (
                    <div className="mt-1 text-xs text-emerald-700 font-medium truncate flex items-center gap-1"><Wrench className="w-3 h-3" />{l.ticket_title}</div>
                  )}
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Wrench className="w-3 h-3" /> {l.active_tickets}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtAgo(l.updated_at, lang)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
